import { ArrowRight, ChevronRight, Instagram } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { CookieSettingsLink } from '../components/CookieConsent';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { Logo, LogoMark } from '../components/Logo';
import { useSession } from '../lib/session';
import { useMarketingCopy } from './content';
import { HUB_PAGES, HUB_PATHS, PAGE_PATHS } from './content/shared';
import { SIZES_PATH, TEMPLATES_PATH } from './programmatic';
import { useLocalePath, usePageLocale } from './PublicRoute';

/** Top bar of the marketing pages (the landing has its own dark hero header). */
export function MarketingHeader() {
  const locale = usePageLocale();
  const { ui } = useMarketingCopy(locale);
  const lp = useLocalePath();
  const user = useSession((s) => s.user);
  return (
    <header className="border-b border-zinc-100 bg-white/80 backdrop-blur">
      <div className="mx-auto flex max-w-7xl items-center gap-4 px-6 py-4 sm:gap-6">
        <Logo to={lp('/')} />
        <nav className="hidden items-center gap-6 text-sm text-zinc-600 md:flex">
          <Link to={lp(HUB_PATHS['use-cases'])} className="hover:text-zinc-900">
            {ui.navUseCases}
          </Link>
          <Link to={lp(TEMPLATES_PATH)} className="hover:text-zinc-900">
            {ui.navTemplates}
          </Link>
          <Link to={lp(HUB_PATHS.compare)} className="hover:text-zinc-900">
            {ui.navCompare}
          </Link>
          <Link to={`${lp('/')}#pricing`} className="hover:text-zinc-900">
            {ui.navPricing}
          </Link>
          <a href="/docs" className="hover:text-zinc-900">
            {ui.navDocs}
          </a>
        </nav>
        <div className="flex-1" />
        <LanguageSwitcher className="hidden sm:inline-flex" />
        {user ? (
          <Link
            to="/app/templates"
            className="inline-flex items-center gap-1.5 rounded-lg bg-zinc-900 px-4 py-2 text-sm font-semibold text-white hover:bg-zinc-700"
          >
            {ui.openApp} <ArrowRight className="size-4" />
          </Link>
        ) : (
          <>
            <Link
              to="/login"
              className="hidden text-sm font-medium text-zinc-600 hover:text-zinc-900 sm:block"
            >
              {ui.signIn}
            </Link>
            <Link
              to="/signup"
              className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700"
            >
              {ui.startFree}
            </Link>
          </>
        )}
      </div>
    </header>
  );
}

/** Footer with links to every use case and comparison (also on the landing). */
export function MarketingFooter() {
  const locale = usePageLocale();
  const { ui, pages, contact } = useMarketingCopy(locale);
  const lp = useLocalePath();
  const column = (
    title: string,
    links: Array<{ to: string; label: string; external?: boolean }>,
    extra?: ReactNode,
  ) => (
    <div>
      <h2 className="text-sm font-semibold text-zinc-900">{title}</h2>
      <ul className="mt-3 space-y-2 text-sm">
        {links.map((l) => (
          <li key={l.to}>
            {l.external ? (
              <a href={l.to} className="text-zinc-500 hover:text-zinc-900">
                {l.label}
              </a>
            ) : (
              <Link to={l.to} className="text-zinc-500 hover:text-zinc-900">
                {l.label}
              </Link>
            )}
          </li>
        ))}
        {extra && <li>{extra}</li>}
      </ul>
    </div>
  );
  return (
    <footer className="border-t border-zinc-200 bg-zinc-50/60">
      <div className="mx-auto grid max-w-7xl gap-10 px-6 py-12 sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <div className="flex items-center gap-2 font-bold text-zinc-900">
            <LogoMark className="size-6" /> Bannermatic
          </div>
          <p className="mt-3 text-sm text-zinc-500">© {new Date().getFullYear()} Bannermatic</p>
          <a
            href="https://www.instagram.com/bannermatic.app"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Instagram"
            title="Instagram @bannermatic.app"
            className="mt-3 -ml-2 inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-sm text-zinc-500 hover:bg-zinc-100 hover:text-zinc-800"
            data-testid="instagram-link"
          >
            <Instagram className="size-4" /> @bannermatic.app
          </a>
        </div>
        {column(
          ui.footerUseCases,
          HUB_PAGES['use-cases'].map((id) => ({ to: lp(PAGE_PATHS[id]), label: pages[id].label })),
        )}
        {column(
          ui.footerCompare,
          HUB_PAGES.compare.map((id) => ({ to: lp(PAGE_PATHS[id]), label: pages[id].label })),
        )}
        {column(
          ui.footerProduct,
          [
            { to: `${lp('/')}#pricing`, label: ui.navPricing },
            { to: '/docs', label: ui.navDocs, external: true },
            { to: lp(TEMPLATES_PATH), label: ui.navTemplates },
            { to: lp(SIZES_PATH), label: ui.navSizes },
            { to: lp('/contact'), label: contact.label },
            { to: lp('/privacy'), label: ui.privacy },
            { to: lp('/terms'), label: ui.terms },
            { to: '/login', label: ui.signIn },
          ],
          <CookieSettingsLink className="text-zinc-500 hover:text-zinc-900" />,
        )}
      </div>
    </footer>
  );
}

export function Breadcrumbs({ items }: { items: Array<{ label: string; to?: string }> }) {
  return (
    <nav aria-label="Breadcrumb" className="text-sm text-zinc-500">
      <ol className="flex flex-wrap items-center gap-1.5">
        {items.map((item, i) => (
          <li key={i} className="flex items-center gap-1.5">
            {i > 0 && <ChevronRight className="size-3.5 text-zinc-400" />}
            {item.to ? (
              <Link to={item.to} className="hover:text-zinc-900">
                {item.label}
              </Link>
            ) : (
              <span aria-current="page" className="text-zinc-700">
                {item.label}
              </span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

export function MarketingShell({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-full bg-white">
      <MarketingHeader />
      <main>{children}</main>
      <MarketingFooter />
    </div>
  );
}
